import { defineStore } from 'pinia'
import { computed, h, reactive, ref, render, watch } from 'vue'
import { useRoute } from 'vue-router'

import { getEntries, waitForElement } from '@/lib/utils'
import { usePDF } from './pdfStore'
import { useNewPdfStore } from './newPdfStore'

import type { FieldObject, FieldObjects } from '@/types'

export interface Signature {
  data_uri: string
  annotation_id: string
  annotation_field_name: string
}

export const useSignatures = defineStore('signatures', () => {
  const route = useRoute()
  const pdf = usePDF()
  const newPdf = useNewPdfStore()

  const showSignModal = ref<boolean>(false)
  const signatureFields = ref<FieldObjects | null>(null)
  const selectedSignatureFieldObject = ref<FieldObject | null>(null)

  const currentPdfStore = ref<'old' | 'new'>('old')
  const manualFormId = ref<string>('')

  const signatureRegexp =
    route.name === 'PDF Template Detail' ||
    route.name === 'External PDF Template Detail' ||
    route.name === 'Legacy Document Detail' ||
    route.name === 'Legacy External Document Detail'
      ? /^(?!.*lawfirm).*signature\d*/i
      : /signature\d*_lawfirm|signature_lawfirm\d*/i

  const signatures = reactive<{ [key: string]: Signature }>({})
  const cancelFunctions = new Map<string, () => void>()

  const hasAllSignatures = computed(() => {
    if (!signatureFields.value) return true
    return Object.values(signatures).length === Object.values(signatureFields.value).length
  })

  function setPdfStore(store: 'old' | 'new') {
    currentPdfStore.value = store
  }

  function setManualFormId(formId: string) {
    manualFormId.value = formId
  }

  async function findAllSignatureFields(fieldObjects: FieldObjects): Promise<void> {
    // Check if we have a selected form in either store
    const hasSelectedForm =
      currentPdfStore.value === 'old' ? pdf.selectedForm : newPdf.selectedPdfs.size > 0

    if (!hasSelectedForm) return

    signatureFields.value = Object.fromEntries(
      getEntries(fieldObjects).filter(([key]) => key.search(signatureRegexp) !== -1)
    )
  }

  function checkSignatures(): boolean {
    if (!signatureFields.value) return true
    const signatureNames = Object.values(signatures).map((x) => x.annotation_field_name)
    return Object.values(signatureFields.value)
      .flat()
      .every((elem) => signatureNames.includes(elem.name))
  }

  function updateSignatureAnnotation(annotation: FieldObject, el: HTMLElement): void {
    let formId = ''

    if (currentPdfStore.value === 'old') {
      if (!pdf.selectedForm) return
      formId = pdf.selectedForm?.documentProperties?.id || ''
    } else {
      // For new PDF store, we need to get the form ID from the current PDF
      // This will be set by the component using the signature store
      if (manualFormId.value) {
        formId = manualFormId.value
      } else if (newPdf.selectedPdfs.size > 0) {
        // Get the first (and likely only) PDF in the new store
        const currentPdf = Array.from(newPdf.selectedPdfs.values())[0]
        formId = currentPdf?.form?.id || ''
      } else {
        return
      }
    }

    const signatureKey = formId + annotation.name

    const signature = signatures[signatureKey]
    if (signature) {
      render(
        h('img', {
          src: signature.data_uri,
          alt: annotation.name,
          class: 'signature',
          style: {
            border: '2px solid #c44747',
            cursor: 'crosshair',
            height: '60px',
            position: 'relative',
            top: currentPdfStore.value === 'new' ? '0px' : '-30px',
            zIndex: -65536
          },
          onClick: () => {
            selectedSignatureFieldObject.value = annotation
            showSignModal.value = !showSignModal.value
          }
        }),
        el
      )
    } else {
      render(
        h(
          'div',
          {
            style: {
              alignItems: 'center',
              backgroundColor: '#c4474777',
              border: '2px dashed #c44747',
              borderRadius: '2px',
              color: '#c44747',
              cursor: 'crosshair',
              objectFit: 'contain',
              overflow: 'hidden',
              padding: '4px',
              position: 'relative',
              top: currentPdfStore.value === 'new' ? '0px' : '0px',
              zIndex: 65536
            },
            onClick: () => {
              selectedSignatureFieldObject.value = annotation
              showSignModal.value = !showSignModal.value
            },
            'v-resize-text': {
              ratio: 1.3,
              minFontSize: '30px',
              maxFontSize: '50px',
              delay: 200
            }
          },
          'Unterschrift'
        ),
        el
      )
    }
  }

  async function updateAll(fieldObjects: FieldObjects): Promise<void> {
    const signatureEntries = getEntries(fieldObjects).filter(([key]) => signatureRegexp.test(key))

    signatureEntries.forEach(([, fieldObjects]) => {
      fieldObjects.forEach((fieldObject) => {
        markSignature(fieldObject)
      })
    })
  }

  async function markSignature(fieldObject: FieldObject | null): Promise<void> {
    if (!fieldObject) {
      console.warn('markSignature: No fieldObject provided')
      return
    }

    const name = `section[data-annotation-id="${fieldObject.id}"]`

    // Cancel any existing operation for this field object
    if (cancelFunctions.has(fieldObject.id)) {
      cancelFunctions.get(fieldObject.id)!()
      cancelFunctions.delete(fieldObject.id)
    }

    const { promise, cancel } = waitForElement(name)
    cancelFunctions.set(fieldObject.id, cancel)

    promise
      .then((el: Element) => {
        console.log('markSignature: Found element for fieldObject:', fieldObject.name)
        updateSignatureAnnotation(fieldObject, el as HTMLElement)
        cancelFunctions.delete(fieldObject.id)
      })
      .catch((error) => {
        console.error('markSignature: Error waiting for element:', error)
        cancelFunctions.delete(fieldObject.id)
      })
  }

  watch(signatures, () => {
    markSignature(selectedSignatureFieldObject.value)
  })

  watch(selectedSignatureFieldObject, () => {
    markSignature(selectedSignatureFieldObject.value)
  })

  function clearSignatures(): void {
    signatureFields.value = null
    selectedSignatureFieldObject.value = null
    showSignModal.value = false
    manualFormId.value = ''
    Object.keys(signatures).forEach((key) => {
      delete signatures[key]
    })
  }

  // New cleanup function
  function cleanup() {
    showSignModal.value = false
    cancelFunctions.forEach((cancel) => cancel())
    cancelFunctions.clear()
  }

  /**
   * Check if a signature exists for a given formId and fieldName.
   * First checks for formId-specific signature, then checks for shared signature from sibling documents.
   *
   * :param formId: The form ID to check
   * :param fieldName: The signature field name to check
   * :return: The signature if found, null otherwise
   */
  function findSignatureForField(formId: string, fieldName: string): Signature | null {
    const signatureKey = formId + fieldName
    const formSpecificSignature = signatures[signatureKey]
    if (formSpecificSignature?.data_uri?.trim()) {
      return formSpecificSignature
    }

    const sharedSignature = Object.values(signatures).find(
      (sig) => sig.annotation_field_name === fieldName && sig.data_uri?.trim()
    )

    return sharedSignature || null
  }

  function getFormSignatures(
    formId: string,
    annotations?: Array<{ name?: string }>
  ): Record<string, { data_uri: string }> {
    const result: Record<string, { data_uri: string }> = {}

    // First, get all signatures for this specific formId
    Object.entries(signatures).forEach(([key, signature]) => {
      if (key.startsWith(formId) && signature.data_uri) {
        const fieldName = key.replace(formId, '')
        result[fieldName] = { data_uri: signature.data_uri }
      }
    })

    // Extract signature field names from annotations if provided
    if (annotations && annotations.length > 0) {
      const signatureFieldNames = annotations
        .filter((ann) => {
          const fieldName = ann.name || ''
          return signatureRegexp.test(fieldName)
        })
        .map((ann) => ann.name || '')
        .filter((name) => name !== '')

      // Check for shared signatures (signatures with the same field name but different formId)
      signatureFieldNames.forEach((fieldName) => {
        // If we already have a signature for this field name, skip
        if (result[fieldName]) {
          return
        }

        // Use helper function to find shared signature
        const sharedSignature = findSignatureForField(formId, fieldName)
        if (sharedSignature) {
          result[fieldName] = { data_uri: sharedSignature.data_uri }
        }
      })
    }

    return result
  }

  async function hasMissingSignatures(formId: string): Promise<boolean> {
    if (!formId) return false

    const pdfState = newPdf.selectedPdfs.get(formId)
    if (!pdfState || !pdfState.loadingTask) return false

    try {
      const pdfDoc = await pdfState.loadingTask.promise
      const fieldObjects = await pdfDoc.getFieldObjects()

      if (!fieldObjects) return false

      const signatureFieldEntries = getEntries(fieldObjects).filter(
        ([key]) => key.search(signatureRegexp) !== -1
      )

      if (signatureFieldEntries.length === 0) return false

      const signatureFieldNames = signatureFieldEntries
        .flatMap(([, fieldObjects]) => fieldObjects)
        .map((field) => field.name)

      return signatureFieldNames.some((fieldName) => {
        // Use helper function to check if signature exists
        const signature = findSignatureForField(formId, fieldName)
        return !signature
      })
    } catch (error) {
      console.warn('Error checking signatures for form:', formId, error)
      return false
    }
  }

  async function getAllMissingSignatures(): Promise<Record<string, boolean>> {
    const result: Record<string, boolean> = {}

    if (currentPdfStore.value === 'new') {
      const promises = Array.from(newPdf.selectedPdfs.keys()).map(async (formId) => {
        const hasMissing = await hasMissingSignatures(formId)
        return { formId, hasMissing }
      })

      const results = await Promise.all(promises)
      results.forEach(({ formId, hasMissing }) => {
        result[formId] = hasMissing
      })
    } else {
      // For old PDF store, check the selected form
      if (pdf.selectedForm?.documentProperties?.id) {
        const formId = pdf.selectedForm.documentProperties.id
        result[formId] = await hasMissingSignatures(formId)
      }
    }

    return result
  }

  return {
    checkSignatures,
    findAllSignatureFields,
    hasAllSignatures,
    selectedSignatureFieldObject,
    showSignModal,
    signatureFields,
    signatures,
    updateAll,
    clearSignatures,
    cleanup,
    setPdfStore,
    setManualFormId,
    manualFormId,
    currentPdfStore,
    getFormSignatures,
    hasMissingSignatures,
    getAllMissingSignatures,
    signatureRegexp,
    findSignatureForField
  }
})
